const normalizeText = (value = "") =>
  String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase()
    .trim();

/* ==========================================================================
   PRODUITS
   ========================================================================== */

export const SITE_PRODUCT_LABELS = {
  standard: "MonAppliClub Standard",
  light: "MonAppliClub Light",

  monapplimairie: "MonAppliMairie",
  monappliasso: "MonAppliAsso",

  monapplitournoi: "MonAppliTournoi",
  judocoachpro: "Judo Coach Pro",
  judopresencepro: "Judo Présence Pro",
  monappliag: "MonAppliAG",

  comite: "MonAppliComité",
  ligue: "MonAppliLigue",

  saas: "SaaS",
  website: "Website",

  unknown: "Autre",
};

export const SITE_PRODUCT_TONES = {
  standard: "sky",
  light: "cyan",

  monapplimairie: "emerald",
  monappliasso: "amber",

  monapplitournoi: "violet",
  judocoachpro: "rose",
  judopresencepro: "emerald",
  monappliag: "amber",

  comite: "cyan",
  ligue: "violet",

  saas: "violet",
  website: "slate",
  unknown: "slate",
};

/* ==========================================================================
   FAMILLES
   ========================================================================== */

export const SITE_FAMILY_LABELS = {
  monappliclub: "MonAppliClub",
  mairie: "MonAppliMairie",
  comite_ligue: "Comités & Ligue",
  saas: "SaaS",
  outils: "Outils spécialisés",
  autres: "Autres sites",
};

export const SITE_FAMILY_DESCRIPTIONS = {
  monappliclub:
    "Sites MonAppliClub Standard et Light.",
  mairie:
    "Applications destinées aux communes et collectivités.",
  comite_ligue:
    "Applications des comités départementaux et des ligues.",
  saas:
    "Applications SaaS ne correspondant pas à une famille métier spécifique.",
  outils:
    "MonAppliTournoi, Judo Coach Pro, Judo Présence Pro et MonAppliAG.",
  autres:
    "Sites web, MonAppliAsso et applications non encore catégorisées.",
};

export const SITE_FAMILY_ORDER = [
  "monappliclub",
  "mairie",
  "comite_ligue",
  "saas",
  "outils",
  "autres",
];

/* ==========================================================================
   PROFILS API

   On sépare volontairement :
   - critical : notifications / messages
   - metrics  : statistiques secondaires

   Une erreur sur une statistique secondaire ne dégrade donc plus la
   surveillance des alertes.
   ========================================================================== */

const STANDARD_PROFILE = {
  critical: {
    notifications: "/api/notifications/1",
    messages: "/api/messages/allmessages/1",
  },

  metrics: {
    visitsByDate: "/api/visites/countVisitesByDate",
    totalUsers: "/api/users/countUsers",
    todayUsers: "/api/users/countUsersToday",
    adherents: "/api/dashboard/comptageStatutLicencie",
    inscrits: "/api/dashboard/comptageInscriptionsTotales",
  },
};

const LIGHT_PROFILE = {
  critical: {
    notifications: "/api/notifications/1",
  },

  metrics: {
    visitsByDate: "/api/visites/countVisitesByDate",
    visitsTotal: "/api/visites/countVisites",
    adherents: "/api/adherents/countAdherents",
  },
};

const SAAS_PROFILE = {
  critical: {
    notifications: "/api/notifications/1",
    messages: "/api/messages/allmessages/1",
  },

  metrics: {
    visitsByDate: "/api/visites/countVisitesByDate",
    totalUsers: "/api/users/countUsers",
    todayUsers: "/api/users/countUsersToday",
  },
};

const MAIRIE_PROFILE = {
  critical: {},

  metrics: {
    visitsByDate: "/api/visites/countVisitesByDate",
    totalUsers: "/api/users/countUsers",
    todayUsers: "/api/users/countUsersToday",
  },
};

const ASSO_PROFILE = {
  critical: {},

  metrics: {
    visitsByDate: "/api/visites/countVisitesByDate",
    totalUsers: "/api/users/countUsers",
    todayUsers: "/api/users/countUsersToday",
  },
};

const WEBSITE_PROFILE = {
  critical: {},

  metrics: {
    visitsByDate: "/api/visites/countVisitesByDate",
  },
};

/*
 * Les applications suivantes sont considérées pour le moment comme
 * "SaaS-like".
 *
 * C'est volontairement centralisé ici : si l'une d'elles a des routes
 * différentes, il suffira de modifier SON profil et rien d'autre dans
 * le dashboard.
 */
const COMITE_PROFILE = {
  ...SAAS_PROFILE,
};

const LIGUE_PROFILE = {
  ...SAAS_PROFILE,
};

const TOURNOI_PROFILE = {
  ...SAAS_PROFILE,
};

const COACH_PRO_PROFILE = {
  ...SAAS_PROFILE,
};

const PRESENCE_PRO_PROFILE = {
  ...SAAS_PROFILE,
};

const AG_PROFILE = {
  ...SAAS_PROFILE,
};

const UNKNOWN_PROFILE = {
  critical: {},

  metrics: {
    visitsByDate: "/api/visites/countVisitesByDate",
  },
};

export const SITE_ACTIVITY_PROFILES = {
  standard: STANDARD_PROFILE,
  light: LIGHT_PROFILE,

  monapplimairie: MAIRIE_PROFILE,
  monappliasso: ASSO_PROFILE,

  monapplitournoi: TOURNOI_PROFILE,
  judocoachpro: COACH_PRO_PROFILE,
  judopresencepro: PRESENCE_PRO_PROFILE,
  monappliag: AG_PROFILE,

  comite: COMITE_PROFILE,
  ligue: LIGUE_PROFILE,

  saas: SAAS_PROFILE,
  website: WEBSITE_PROFILE,

  unknown: UNKNOWN_PROFILE,
};

/* ==========================================================================
   DÉTECTION DU PRODUIT
   ========================================================================== */

export function resolveSiteProductKey(asso) {
  const product = normalizeText(asso?.produit_asso);
  const name = normalizeText(asso?.nom_asso);

  const combined = `${product} ${name}`;

  /*
   * Les produits métier spécifiques passent AVANT SaaS,
   * car certains peuvent être enregistrés produit_asso="SaaS".
   */

  if (
    combined.includes("MONAPPLITOURNOI") ||
    combined.includes("MON APPLI TOURNOI")
  ) {
    return "monapplitournoi";
  }

  if (
    combined.includes("JUDO COACH PRO") ||
    combined.includes("JUDOCOACHPRO")
  ) {
    return "judocoachpro";
  }

  if (
    combined.includes("JUDO PRESENCE PRO") ||
    combined.includes("JUDO PRESENCE") ||
    combined.includes("JUDOPRESENCEPRO")
  ) {
    return "judopresencepro";
  }

  if (
    combined.includes("MONAPPLIAG") ||
    combined.includes("MON APPLI AG")
  ) {
    return "monappliag";
  }

  if (
    combined.includes("MONAPPLICOMITE") ||
    combined.includes("MON APPLI COMITE") ||
    product.includes("COMITE") ||
    name.includes("COMITE DE JUDO") ||
    name.includes("COMITE DEPARTEMENTAL")
  ) {
    return "comite";
  }

  if (
    combined.includes("MONAPPLILIGUE") ||
    product.includes("LIGUE") ||
    name.includes("LIGUE DE JUDO") ||
    name.includes("LIGUE NOUVELLE AQUITAINE")
  ) {
    return "ligue";
  }

  if (
    product.includes("MONAPPLIASSO") ||
    name.includes("MONAPPLIASSO")
  ) {
    return "monappliasso";
  }

  if (
    product.includes("MONAPPLIMAIRIE") ||
    product.includes("MAIRIE") ||
    product.includes("COMMUNE") ||
    name.includes("MONAPPLIMAIRIE")
  ) {
    return "monapplimairie";
  }

  if (
    product === "MONAPPLICLUB STANDARD" ||
    product === "STANDARD"
  ) {
    return "standard";
  }

  if (
    product === "MONAPPLICLUB LIGHT" ||
    product === "LIGHT"
  ) {
    return "light";
  }

  if (product === "SAAS") {
    return "saas";
  }

  if (product === "WEBSITE") {
    return "website";
  }

  return "unknown";
}

/* ==========================================================================
   FAMILLE DU PRODUIT
   ========================================================================== */

export function resolveSiteFamilyKey(productKey) {
  switch (productKey) {
    case "standard":
    case "light":
      return "monappliclub";

    case "monapplimairie":
      return "mairie";

    case "comite":
    case "ligue":
      return "comite_ligue";

    case "saas":
      return "saas";

    case "monapplitournoi":
    case "judocoachpro":
    case "judopresencepro":
    case "monappliag":
      return "outils";

    default:
      return "autres";
  }
}

/* ==========================================================================
   URL
   ========================================================================== */

export function hasValidApiUrl(asso) {
  return (
    !!asso?.apiUrl_asso &&
    /^https?:\/\//i.test(String(asso.apiUrl_asso))
  );
}

function getHostFromUrl(url = "") {
  try {
    return new URL(url)
      .hostname
      .replace(/^www\./, "")
      .toLowerCase();
  } catch {
    return "";
  }
}

export function isMainMonAppliClubSite(asso) {
  const host = getHostFromUrl(asso?.siteweb_asso || "");

  return host === "monappliclub.com";
}

/* ==========================================================================
   HOME
   ========================================================================== */

export const HOME_PAGE_ALIASES = [
  "/",
];

/* ==========================================================================
   TARGET
   ========================================================================== */

export function buildSiteTargetFromAssociation(asso) {
  const productKey = resolveSiteProductKey(asso);
  const familyKey = resolveSiteFamilyKey(productKey);

  const profile =
    SITE_ACTIVITY_PROFILES[productKey] ||
    SITE_ACTIVITY_PROFILES.unknown;

  const criticalPaths = {
    ...(profile.critical || {}),
  };

  const metricPaths = {
    ...(profile.metrics || {}),
  };

  const isMainSite = isMainMonAppliClubSite(asso);

  /*
   * Exception historique.
   */
  if (
    Number(asso?.id) === 1 &&
    criticalPaths.notifications &&
    criticalPaths.messages
  ) {
    criticalPaths.notifications = "/api/notifications/50";
    criticalPaths.messages = "/api/messages/allmessages/50";
  }

  /*
   * www.monappliclub.com :
   * visites du jour = uniquement page d'accueil.
   *
   * On conserve visitsByDate pour connaître le total historique.
   */
  if (isMainSite) {
    metricPaths.pageVisitsToday =
      "/api/visites/countVisitesByPageToday";
  }

  return {
    id: asso.id,
    associationId: asso.id,

    name:
      asso.nom_asso ||
      "Site sans nom",

    productKey,

    productLabel:
      SITE_PRODUCT_LABELS[productKey] ||
      SITE_PRODUCT_LABELS.unknown,

    familyKey,

    familyLabel:
      SITE_FAMILY_LABELS[familyKey] ||
      SITE_FAMILY_LABELS.autres,

    logo:
      asso.logo_asso ||
      "",

    appUrl:
      asso.siteweb_asso ||
      "",

    apiUrl:
      asso.apiUrl_asso ||
      "",

    criticalPaths,
    metricPaths,

    hasCriticalMonitoring:
      Object.keys(criticalPaths).length > 0,

    visitsMode:
      isMainSite
        ? "homeOnly"
        : "default",

    visitsModeLabel:
      isMainSite
        ? "Accueil uniquement"
        : "Toutes visites",

    rawAssociation: asso,
  };
}